import {
  useCreateActivity,
  useGetAgentActivityType,
  useGetAgentContactType
} from 'common/queries-and-mutations/admin/prospect';
import Button from 'components/Form/Button';
import CustomSelect from 'components/Form/CustomSelect';
import TextAreaFieldWithAutoHeightResize from 'components/Form/TextAreaFieldWithAutoHeightResize';
import withCreatePortal from 'components/HOC/withCreatePortal';
import { Icon, Icons } from 'components/Icon';
import SuccessModal from 'components/Modal/SuccessModal';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextField from 'components/Form/TextField';
import { format } from 'date-fns';
import { useGetPlans } from 'common/queries-and-mutations/payment';

const EnhancedSuccessModal = withCreatePortal(SuccessModal);

export default function CreateNote() {
  const { prospectId } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const { data: plans } = useGetPlans();

  const { mutate, isSuccess, isError } = useCreateActivity();
  const { data: activityTypes } = useGetAgentActivityType();
  const { data: contactTypes } = useGetAgentContactType();

  const formik = useFormik({
    initialValues: {
      narration: '',
      activityType: { value: '', label: '' },
      contactType: { value: '', label: '' },
      location: '',
      contactDate: '',
      nextVisitDate: '',
      productName: ''
    },
    validationSchema: Yup.object().shape({
      narration: Yup.string().required('Narration is required'),
      productName: Yup.string().required('Product name is required'),
      activityType: Yup.object()
        .shape({
          label: Yup.string().required('Activity Type is required'),
          value: Yup.number().required('Activity Type is required')
        })
        .nullable(),
      contactType: Yup.object().shape({
        label: Yup.string().required('Contact Type is required'),
        value: Yup.number().required('Contact Type is required')
      }),
      location: Yup.string().required('Location is required').nullable(),
      contactDate: Yup.date().required('Contact date is required').nullable()
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      handleSubmit(values);
    }
  });

  const handleSubmit = (values: any) => {
    setIsSubmitting(true);
    const payload = {
      prospect_id: Number(prospectId),
      productName: values.productName,
      narration: values.narration,
      nextVisitDate_mm_dd_yyyy: values.nextVisitDate
        ? format(new Date(values.nextVisitDate), 'MM-dd-yyyy')
        : '',
      contactDate_mm_dd_yyyy: format(
        new Date(values.contactDate),
        'MM-dd-yyyy'
      ),
      contact_type: Number(values.contactType.value),
      activity_type: Number(values.activityType.value),
      location: values.location
    };
    mutate(payload);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      setShowSuccess(true);
    } else if (isError) {
      setIsSubmitting(false);
    }
  }, [isError, isSuccess]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <Breadcrumb title="Make a Note" />
        <Link
          to={`/admin/prospects/notes/${prospectId}`}
          className="flex items-center gap-4"
        >
          <Icon name={Icons.ArrowLeft} />
          <span className="text-base font-bold text-primary-800">
            Back to Notes
          </span>
        </Link>
      </div>

      <div className="z-0 mb-6 mt-3 h-full w-full rounded-lg border-[1.5px] border-[#F2F2F2] bg-white">
        <form onSubmit={formik.handleSubmit} className="h-full">
          <div className="m-5">
            <div className="grid md:grid-cols-2 md:gap-x-10">
              <div className="group relative z-0 mb-6 w-full">
                <CustomSelect
                  options={activityTypes?.keyDatavalues.map(type => ({
                    value: type.code,
                    label: type.desc
                  }))}
                  selectedOption={formik.values.activityType}
                  handleOptionChange={option => {
                    formik.setFieldValue(`activityType`, option);
                  }}
                  label="Activity Type"
                  name="activityType"
                  isRequired
                  isSearchable
                  className="h-[3.5rem] rounded-2xl"
                  error={
                    'activityType' in formik.errors
                      ? 'Please select activity type'
                      : ''
                  }
                />
              </div>
              <div className="group relative z-0 mb-6 w-full">
                <CustomSelect
                  options={contactTypes?.keyDatavalues.map(type => ({
                    value: type.code,
                    label: type.desc
                  }))}
                  selectedOption={formik.values.contactType}
                  handleOptionChange={option => {
                    formik.setFieldValue(`contactType`, option);
                  }}
                  label="Contact Type"
                  name="contactType"
                  isRequired
                  isSearchable
                  className="h-[3.5rem] rounded-2xl"
                  error={
                    'contactType' in formik.errors
                      ? 'Please select contact type'
                      : ''
                  }
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 md:gap-6">
              <div className="group relative z-0 mb-6 w-full">
                <TextField
                  name={`location`}
                  placeholder=""
                  isRequired
                  value={formik.values.location}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Location"
                  error={formik.touched.location ? formik.errors.location : ''}
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                />
              </div>
              <div className="group relative z-0 mb-6 w-full">
                <TextField
                  name={`contactDate`}
                  placeholder=""
                  value={formik.values.contactDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Contact Date"
                  isRequired
                  error={
                    formik.touched.contactDate ? formik.errors.contactDate : ''
                  }
                  type="date"
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                  validateDate={false}
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 md:gap-6">
              <div className="group relative z-0 mb-6 w-full">
                <TextField
                  name={`nextVisitDate`}
                  placeholder=""
                  value={formik.values.nextVisitDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Next Visit Date"
                  error={
                    formik.touched.nextVisitDate
                      ? formik.errors.nextVisitDate
                      : ''
                  }
                  type="date"
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                  validateDate={false}
                />
              </div>
              <div className="group relative z-0 mb-6 w-full">
                <CustomSelect
                  options={(plans ?? []).map(plan => ({
                    value: plan.planType,
                    label: plan.planType
                  }))}
                  selectedOption={{
                    value: formik.values.productName,
                    label: formik.values.productName
                  }}
                  handleOptionChange={option => {
                    formik.setFieldValue(`productName`, option?.value);
                  }}
                  label="Product Name"
                  name="productName"
                  isRequired
                  isSearchable
                  className="h-[3.5rem] rounded-2xl"
                  error={
                    formik.touched.productName ? formik.errors.productName : ''
                  }
                />
              </div>
            </div>
            <TextAreaFieldWithAutoHeightResize
              label="Narration"
              name={`narration`}
              value={formik.values.narration}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Type here"
              isRequired
              rows={25}
              className="w-full rounded"
              error={formik.touched.narration ? formik.errors.narration : ''}
            />
          </div>
          <div className="mx-5 flex justify-end">
            <Button
              variant="outline"
              size="custom"
              className="place-self-end rounded bg-primary-800 px-5 py-1.5 text-center text-xs font-medium text-white"
              label="Save Note"
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            ></Button>
          </div>
        </form>
      </div>

      {showSuccess && (
        <EnhancedSuccessModal
          title="Note added Successfully"
          onProccessClick={() => {
            setShowSuccess(false);
            navigate(`/admin/prospects/notes/${prospectId}`);
          }}
          buttonText="Close"
        />
      )}
    </div>
  );
}
